import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import queryString from 'query-string'
import FlexRow from '../../../components/molecules/FlexRow'
import { fromURLSafeBase64 } from '../../../utils/URLSafeBase64'
import FlexColumn from '../../../components/molecules/FlexColumn'
import { connections } from '../../../api/integrations'
import { useGetPendingAuthorizationStatus } from '../../../api/users/authorizations'
import Loading from '../../../components/molecules/Loading'

const USER_CALLBACK_MESSAGE_TYPE = 'denali:oauth-user-callback'
const USER_STATE_PREFIX = 'u.'

function CallbackUtility () {
  const { provider } = useParams()
  const location = useLocation()
  const { mutateAsync: resolveAuthorization } = connections.useExchangeConnectionAuthorization()

  const queryParams = useMemo(() => queryString.parse(location?.search || ''), [location?.search])
  const rawState = queryParams?.state || ''
  const isUserFlow = typeof rawState === 'string' && rawState.startsWith(USER_STATE_PREFIX)
  const authorizationId = useMemo(() => {
    if (isUserFlow || !rawState) return null
    try {
      return fromURLSafeBase64(rawState)
    } catch (err) {
      console.warn('CallbackUtility: failed to decode state; treating as invalid', err)
      return null
    }
  }, [isUserFlow, rawState])
  const code = queryParams?.code
  const realmId = queryParams?.realmId
  const { data: authorizationStatus } = useGetPendingAuthorizationStatus(
    isUserFlow ? null : authorizationId,
    isUserFlow ? null : provider
  )

  const [completed, setCompleted] = useState(false)
  const [userRelayStatus, setUserRelayStatus] = useState('relaying')

  useEffect(() => {
    if (!isUserFlow) return

    const message = {
      type: USER_CALLBACK_MESSAGE_TYPE,
      provider,
      code: queryParams?.code || null,
      state: rawState || null,
      error: queryParams?.error || null,
      errorDescription: queryParams?.error_description || null
    }

    try {
      if (window.opener) {
        window.opener.postMessage(message, window.location.origin)
        setUserRelayStatus('relayed')
        setTimeout(() => {
          try { window.close() } catch (_err) { /* popup already closed */ }
        }, 250)
      } else {
        setUserRelayStatus('no-opener')
      }
    } catch (err) {
      console.error('Failed to post message to opener', err)
      setUserRelayStatus('error')
    }
  }, [isUserFlow, provider, queryParams, rawState])
  const handleResolveAuthorization = useCallback(async (authorizationId, purpose, payload, authRequiredFields) => {
    const result = await resolveAuthorization({
      authorizationId,
      fromOrigin: window.location.origin,
      domain: purpose,
      data: {
        ...payload,
        ...authRequiredFields
      }
    })

    if (result?.authorized) {
      setCompleted(true)
      window.close()
    } else if (result?.authorized === false && result.redirect) {
      console.trace('Redirecting to other origin (not normal)')
      const originalURL = new URL(result.redirect)
      if (originalURL.origin !== window.location.origin) {
        const redirectURL = new URL(window.location)
        window.location.href = `${originalURL.origin}${redirectURL.pathname}${redirectURL.search}${redirectURL.hash}`
      }
    } else {
      alert('Not authorized!')
    }
  }, [resolveAuthorization, setCompleted])

  useEffect(() => {
    if (isUserFlow) return
    if (!authorizationStatus) {
      return
    }

    handleResolveAuthorization(
      authorizationId,
      provider,
      {
        code: queryParams?.code,
        realmId: queryParams?.realmId // Specific to QuickBooks
      }
    ).catch(console.error)
  }, [isUserFlow, authorizationStatus, handleResolveAuthorization, authorizationId, provider, code, realmId, queryParams?.code, queryParams?.realmId])

  if (isUserFlow) {
    return (
      <FlexRow className='p-xl justify-center' style={{ minHeight: '400px' }}>
        <FlexColumn>
          {userRelayStatus === 'relayed' ? (
            <>
              <div className='font-size-lg'>Connected</div>
              <div style={{ fontStyle: 'italic' }}>You can close this window</div>
            </>
          ) : userRelayStatus === 'no-opener' ? (
            <>
              <div className='font-size-lg'>Missing opener window</div>
              <div style={{ fontStyle: 'italic' }}>Please close this window and try again from the original tab.</div>
            </>
          ) : userRelayStatus === 'error' ? (
            <>
              <div className='font-size-lg'>Something went wrong</div>
              <div style={{ fontStyle: 'italic' }}>Please close this window and try again.</div>
            </>
          ) : (
            <div>Finishing connection...</div>
          )}
        </FlexColumn>
      </FlexRow>
    )
  }

  return (
    <FlexRow className='p-xl justify-center' style={{ minHeight: '400px' }}>
      <FlexColumn>
        {completed ? (
          <>
            <div className='font-size-lg'>Authorization Complete</div>
            <div style={{ fontStyle: 'italic' }}>You can close this window</div>
          </>
        ) : (
          <Loading />
        )}
      </FlexColumn>
    </FlexRow>
  )
}

export default CallbackUtility
